import React, { createContext, useContext, useCallback, useEffect, useState } from "react";
import { subscriptionApi } from "@/services/api";
import { useAuth } from "@/context/AuthContext";

const PlanContext = createContext(undefined);

// Lightweight tenant-plan provider for the staff web app. Fetches /subscription
// once so locked screens can show an "Upgrade to unlock" banner and the report
// flow knows it's view-only — without every page re-requesting the plan.
export function PlanProvider({ children }) {
  const { user, token } = useAuth();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await subscriptionApi.get();
      setPlan(res.data);
    } catch {
      /* non-fatal — default to no gating if the plan can't be read */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) refresh();
    else setLoading(false);
  }, [token, refresh]);

  const isPlatform = !!user?.is_platform_admin;
  const features = plan?.features || [];
  const has = useCallback(
    (feature) => isPlatform || !plan || features.includes(feature),
    [isPlatform, plan, features],
  );

  const value = {
    plan,
    loading,
    refresh,
    has,
    viewOnly: !isPlatform && !!plan?.view_only_reports,
    isTrial: plan?.source === "free",
    source: plan?.source,
    tier: plan?.tier,
    tierLabel: plan?.tier_label,
    daysLeft: plan?.days_left,
  };
  return <PlanContext.Provider value={value}>{children}</PlanContext.Provider>;
}

export function usePlan() {
  // Safe default outside the provider (portal/contractor shells): never gate.
  return (
    useContext(PlanContext) || {
      plan: null,
      loading: false,
      has: () => true,
      viewOnly: false,
      isTrial: false,
      refresh: () => {},
    }
  );
}
